import type { SalesPipelineEntry, SalesPipelineStage, SalesPipelineStageSummary } from '../../../types';

export const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

export const normalizeStageSummary = (value: unknown): SalesPipelineStageSummary => {
  if (value && typeof value === 'object') {
    const summary = value as Partial<SalesPipelineStageSummary>;
    const count = isFiniteNumber(summary.count) ? summary.count : Number(summary.count ?? 0);
    const totalValue = isFiniteNumber(summary.value) ? summary.value : Number(summary.value ?? 0);
    return {
      count: Number.isFinite(count) ? count : 0,
      value: Number.isFinite(totalValue) ? totalValue : 0,
    };
  }
  return { count: 0, value: 0 };
};

export const formatShortCurrency = (value: number): string => {
  const numeric = Number(value ?? 0);
  if (!Number.isFinite(numeric) || numeric === 0) {
    return '';
  }
  const abs = Math.abs(numeric);
  const sign = numeric < 0 ? '-' : '';
  const abbreviate = (divisor: number, suffix: string) =>
    `${sign}${(abs / divisor).toFixed(1).replace(/\.0$/, '')}${suffix}`;
  if (abs >= 1_000_000_000) return abbreviate(1_000_000_000, 'B');
  if (abs >= 1_000_000) return abbreviate(1_000_000, 'M');
  if (abs >= 1_000) return abbreviate(1_000, 'K');
  return `${sign}${Math.round(abs).toLocaleString()}`;
};

export const getPipelineStageValue = (entry: SalesPipelineEntry): number => {
  const stage = (entry.current_stage ?? 'ENQUIRY') as SalesPipelineStage;
  const values = entry.stage_values || {};
  switch (stage) {
    case 'INVOICED':
      return Number(values.net_invoice_value ?? 0);
    case 'SALES_ORDER':
      return Number(values.sales_order_value ?? 0);
    case 'OPPORTUNITY':
      return Number(values.opportunity_value ?? 0);
    case 'QUOTATION':
      return Number(values.quotation_value ?? 0);
    case 'ENQUIRY':
    default:
      return Number(values.quotation_value ?? 0);
  }
};

export const parseInvoiceDate = (value?: string): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const parseChurnAmount = (value?: string | number): number => {
  if (value === undefined || value === null || value === '') return 0;
  if (typeof value === 'number') return value;
  const trimmed = value.trim();
  if (/m$/i.test(trimmed)) return parseFloat(trimmed) * 1_000_000;
  if (/k$/i.test(trimmed)) return parseFloat(trimmed) * 1_000;
  const numeric = parseFloat(trimmed.replace(/[^0-9.-]+/g, ''));
  return Number.isFinite(numeric) ? numeric : 0;
};

export const formatInvoiceDate = (date: Date): string =>
  date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

const parseDateTimeString = (value: string): Date | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const [datePart, timePart = '00:00:00'] = trimmed.split(/[ T]/);
  if (!datePart) return null;
  const [yearStr, monthStr, dayStr] = datePart.split('-');
  const [hourStr = '0', minuteStr = '0', secondStr = '0'] = timePart.split(':');
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  const hour = Number(hourStr);
  const minute = Number(minuteStr);
  const second = Number(secondStr);
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return null;
  const date = new Date(year, month - 1, day, hour, minute, second);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const parseOpenSODates = (value?: string): Date[] => {
  if (!value) return [];
  return value
    .split(',')
    .map(parseDateTimeString)
    .filter((d): d is Date => Boolean(d));
};

export const computeOpenSOMetrics = (openSo: string | undefined, invoiceDate: Date | null) => {
  const dates = parseOpenSODates(openSo);
  if (!dates.length || !invoiceDate) {
    return { before: 0, after: 0, total: dates.length, conversionRate: null };
  }
  let before = 0;
  let after = 0;
  dates.forEach((date) => {
    if (date.getTime() > invoiceDate.getTime()) {
      after += 1;
    } else {
      before += 1;
    }
  });
  const total = before + after;
  const conversionRate = total > 0 ? (after / total) * 100 : null;
  return { before, after, total, conversionRate };
};

export const formatRelativeDate = (date: Date): string => {
  const now = new Date();
  let diffMs = now.getTime() - date.getTime();
  const inFuture = diffMs < 0;
  diffMs = Math.abs(diffMs);
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 1) return inFuture ? 'in moments' : 'just now';
  if (minutes < 60) return inFuture ? `in ${minutes} min` : `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return inFuture ? `in ${hours} hr` : `${hours} hr ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return inFuture ? `in ${days} days` : `${days} days ago`;
  const months = Math.round(days / 30);
  if (months < 12) return inFuture ? `in ${months} mo` : `${months} mo ago`;
  const years = Math.round(months / 12);
  return inFuture ? `in ${years} yr` : `${years} yr ago`;
};

export const getRevenueTierColor = (amt: number): string =>
  amt > 1_000_000 ? '#2e7d32' : amt >= 500_000 ? '#1a73e8' : '#f29900';
